import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import { useCreateTicket, useUsers, useContacts, useProducts } from '@/hooks/use-api'
import { RecordCombobox } from '@/components/ui/record-combobox'
import { useDebounce } from '@/hooks/use-debounce'
import { useUser } from '@/contexts/UserContext'
import type { Ticket, TicketStatus, TicketPriority, TicketChannel } from '@/types'
import { explainError } from '@/lib/explain-error'

interface CreateTicketModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  accountId?: string
  contactId?: string
  onCreated?: (ticket: Ticket) => void
}

const PRIORITIES: TicketPriority[] = ['Low', 'Medium', 'High', 'Urgent']
const CHANNELS: TicketChannel[] = ['manual', 'email', 'phone', 'chat', 'web']

export function CreateTicketModal({
  open,
  onOpenChange,
  accountId,
  contactId,
  onCreated,
}: CreateTicketModalProps) {
  const { toast } = useToast()
  const { currentUser } = useUser()
  const createMutation = useCreateTicket()

  const [contactSearch, setContactSearch] = useState('')
  const [productSearch, setProductSearch] = useState('')
  const debouncedContactSearch = useDebounce(contactSearch, 300)
  const debouncedProductSearch = useDebounce(productSearch, 300)

  const { data: contactsData, isFetching: contactsLoading } = useContacts({
    search: debouncedContactSearch || undefined,
    limit: 10000,
    sort_by: 'last_name',
    sort_order: 'asc',
    account_id: accountId || undefined,
  })
  const { data: productsData, isFetching: productsLoading } = useProducts({
    search: debouncedProductSearch || undefined,
    limit: 10000,
    sort_by: 'name',
    sort_order: 'asc',
  })
  const { data: usersData } = useUsers({ limit: 10000 })

  const contacts = contactsData?.data || []
  const products = productsData?.data || []
  const users = usersData?.data || []

  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    status: 'New' as TicketStatus,
    priority: 'Medium' as TicketPriority,
    channel: 'manual' as TicketChannel,
    category: '',
    account_id: '',
    contact_id: '',
    product_id: '',
    owner_id: '',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Pre-fill when modal opens
  useEffect(() => {
    if (open) {
      setFormData((prev) => ({
        ...prev,
        account_id: accountId || '',
        contact_id: contactId || '',
        owner_id: currentUser?.id || '',
      }))
    }
  }, [open, accountId, contactId, currentUser?.id])

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => {
        const n = { ...prev }
        delete n[field]
        return n
      })
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const newErrors: Record<string, string> = {}
    if (!formData.subject.trim()) newErrors.subject = 'Subject is required'
    if (!formData.owner_id) newErrors.owner_id = 'Owner is required'

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields.',
        variant: 'destructive',
      })
      return
    }

    try {
      const ticket = await createMutation.mutateAsync({
        ...formData,
        account_id: formData.account_id || undefined,
        contact_id: formData.contact_id || undefined,
        product_id: formData.product_id || undefined,
        category: formData.category || undefined,
      })

      toast({
        title: 'Ticket Created',
        description: `GRK-${ticket.ticket_number} has been created`,
      })
      if (onCreated) onCreated(ticket as Ticket)

      // Reset and close
      setFormData({
        subject: '',
        description: '',
        status: 'New',
        priority: 'Medium',
        channel: 'manual',
        category: '',
        account_id: accountId || '',
        contact_id: '',
        product_id: '',
        owner_id: currentUser?.id || '',
      })
      setErrors({})
      onOpenChange(false)
    } catch (error) {
      toast({
        title: 'Error',
        description: explainError(error, 'Failed to create ticket'),
        variant: 'destructive',
      })
    }
  }

  const selectClassName =
    'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        aria-describedby="create-ticket-description"
      >
        <DialogHeader>
          <DialogTitle>Create New Ticket</DialogTitle>
          <DialogDescription id="create-ticket-description">
            Create a support ticket for this account. Fields marked with * are required.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Subject */}
          <div>
            <Label htmlFor="ticket-subject">Subject *</Label>
            <Input
              id="ticket-subject"
              value={formData.subject}
              onChange={(e) => handleChange('subject', e.target.value)}
              placeholder="Brief description of the issue"
              aria-required="true"
              aria-invalid={!!errors.subject}
              aria-describedby={errors.subject ? 'subject-error' : undefined}
            />
            {errors.subject && (
              <p id="subject-error" className="text-sm text-destructive mt-1" role="alert">
                {errors.subject}
              </p>
            )}
          </div>

          {/* Description */}
          <div>
            <Label htmlFor="ticket-description">Description</Label>
            <textarea
              id="ticket-description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              placeholder="Detailed description of the issue..."
              aria-label="Ticket description"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Priority */}
            <div>
              <Label htmlFor="ticket-priority">Priority</Label>
              <select
                id="ticket-priority"
                value={formData.priority}
                onChange={(e) => handleChange('priority', e.target.value)}
                className={selectClassName}
                aria-label="Ticket priority"
              >
                {PRIORITIES.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>

            {/* Channel */}
            <div>
              <Label htmlFor="ticket-channel">Channel</Label>
              <select
                id="ticket-channel"
                value={formData.channel}
                onChange={(e) => handleChange('channel', e.target.value)}
                className={selectClassName}
                aria-label="Ticket channel"
              >
                {CHANNELS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            {/* Contact */}
            <div>
              <Label>Contact</Label>
              <RecordCombobox
                id="ticket-contact"
                records={contacts}
                value={formData.contact_id}
                onValueChange={(v) => handleChange('contact_id', v)}
                placeholder="Select contact..."
                searchPlaceholder="Search contacts..."
                emptyMessage="No contacts found."
                displayField={(c) => c.name || `${c.first_name} ${c.last_name}`}
                onSearchChange={setContactSearch}
                isLoading={contactsLoading}
                aria-label="Contact"
                totalCount={contactsData?.total}
              />
            </div>

            {/* Product */}
            <div>
              <Label>Product</Label>
              <RecordCombobox
                id="ticket-product"
                records={products}
                value={formData.product_id}
                onValueChange={(v) => handleChange('product_id', v)}
                placeholder="Select product..."
                searchPlaceholder="Search products..."
                emptyMessage="No products found."
                displayField={(p) => p.name || ''}
                onSearchChange={setProductSearch}
                totalCount={productsData?.total}
                isLoading={productsLoading}
                aria-label="Product"
              />
            </div>

            {/* Owner */}
            <div>
              <Label htmlFor="ticket-owner">Owner *</Label>
              <select
                id="ticket-owner"
                value={formData.owner_id}
                onChange={(e) => handleChange('owner_id', e.target.value)}
                className={selectClassName}
                aria-required="true"
                aria-invalid={!!errors.owner_id}
              >
                <option value="">Select owner...</option>
                {users
                  .filter(
                    (u) =>
                      ['User', 'Admin', 'Super Admin'].includes(u.user_type) &&
                      u.is_active !== false
                  )
                  .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                  .map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
              </select>
              {errors.owner_id && (
                <p className="text-sm text-destructive mt-1" role="alert">
                  {errors.owner_id}
                </p>
              )}
            </div>

            {/* Category */}
            <div>
              <Label htmlFor="ticket-category">Category</Label>
              <Input
                id="ticket-category"
                value={formData.category}
                onChange={(e) => handleChange('category', e.target.value)}
                placeholder="e.g. Billing, Technical"
                aria-label="Ticket category"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={createMutation.isPending}
              aria-label="Cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createMutation.isPending}
              aria-label={createMutation.isPending ? 'Creating ticket...' : 'Create ticket'}
            >
              {createMutation.isPending ? 'Creating...' : 'Create Ticket'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
